import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { GARAGE_STORAGE_KEY, evaluateFitment, loadGarage } from '@theme/motivelle-vehicle-state';
import { purchaseGateDecision } from '@theme/motivelle-catalog-context';

export const VEHICLE_CHANGE_EVENT = 'motivelle:vehicle-change';

function parseApplications(element) {
  try {
    const parsed = JSON.parse(element?.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Copy is deliberately conditional and never claims the part is wrong for
// the vehicle: "Not verified" describes missing evidence, not a mismatch.
// See docs/superpowers/specs/2026-09-08-motivelle-first-product-catalog-design.md.
const LIVE_REGION_MESSAGES = {
  vehicle_required: 'Select your vehicle, or confirm the OE number yourself to continue.',
  oem_confirmation_required: 'Confirm you have checked the OE number before adding to cart.',
  not_verified_confirmation_required: 'This application is not independently verified yet. Confirm you have checked the OE number before adding to cart.',
  expert_verification_required: 'This part requires expert verification before it can be purchased.',
  invalid_risk_level: 'This product cannot be purchased right now.',
  invalid_fitment_state: 'This product cannot be purchased right now.'
};

/**
 * Enforces the tiered Fitment purchase gate (Task 2's pure purchaseGateDecision)
 * against Horizon's existing Product Form -- without editing product-form.js.
 * It never duplicates Cart AJAX or availability logic. Enforcement is
 * primarily via DOM state Horizon's own code already respects:
 *   - button.disabled on the same <add-to-cart-component>, which Horizon's
 *     own ProductFormComponent.handleSubmit checks and bails out on
 *     regardless of listener ordering -- this is the load-bearing gate;
 *   - hidden on any .shopify-payment-button/.accelerated-checkout-block,
 *     which removes those buttons from the page entirely (nothing to race).
 * Crucially, ordinary variant selection fires Horizon's own ThemeEvents.variantUpdate
 * handler (assets/product-form.js), which disables Add to Cart for a
 * sold-out/unavailable variant and re-enables it for an available one -- so
 * this gate must both react to the same event and never blindly overwrite
 * Horizon's own stock-based decision. A canPurchase:true Fitment decision is
 * an additional permission, not an override: this gate tracks Horizon's own
 * variant availability from the event payload (`event.detail.resource.available`)
 * and ANDs it into every button-state write, so it can only ever add
 * restriction on top of Horizon's native stock gate, never remove it.
 * A `submit` capture listener is also registered as an extra best-effort
 * safety net for submit paths that do not go through the disabled button
 * (e.g. a future accelerated-checkout SDK); it is not guaranteed to run
 * before Horizon's own document-level capture handler, so it is defense in
 * depth on top of the button-state gate above, never the sole protection.
 */
export class MotivellePurchaseGate extends Component {
  requiredRefs = ['applicationsData', 'buyingByOem', 'acknowledgementContainer', 'acknowledgement', 'liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    this.buyingByOem = false;
    this.acknowledged = false;
    // Browsers can restore a ticked checkbox on reload/back without firing
    // change; keep the visible tick in sync with the gate's own state so a
    // shopper never sees "ticked" next to a disabled Add to cart.
    this.refs.acknowledgement.checked = false;
    this.productForm =this.closest('.product-details')?.querySelector('product-form-component') ?? null;
    this.form = this.productForm?.querySelector('form') ?? null;
    // Read Horizon's own server-rendered availability signal (the button's
    // disabled state) before this gate's own render() ever runs, so the
    // very first decision this gate applies already respects whatever
    // stock state Horizon rendered for the initially selected variant.
    this.variantAvailable = this.readInitialVariantAvailability();
    this.guardSubmit = this.guardSubmit.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.form?.addEventListener('submit', this.guardSubmit, true);
    // Listens on document (not a closer ancestor) so this always runs after
    // Horizon's own ProductFormComponent variant-update handler, which is
    // registered on the closest .shopify-section and updates Add to Cart's
    // disabled state for the new variant's stock first.
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    window.addEventListener(VEHICLE_CHANGE_EVENT, this.handleVehicleChange);
    window.addEventListener('storage', this.handleStorage);
    this.render();
  }

  readInitialVariantAvailability() {
    if (!this.form) return true;
    const button = this.form.querySelector('add-to-cart-component [ref="addToCartButton"]');
    // No button to read means nothing to preserve; default to available so
    // this gate's own Fitment decision is the only thing governing it.
    return button ? !button.disabled : true;
  }

  updatedCallback() {
    super.updatedCallback();
    this.render();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.guardSubmit, true);
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    window.removeEventListener(VEHICLE_CHANGE_EVENT, this.handleVehicleChange);
    window.removeEventListener('storage', this.handleStorage);
    super.disconnectedCallback();
  }

  handleVariantUpdate(event) {
    // This gate listens on `document` (not a closer ancestor) so it always
    // runs after Horizon's own per-instance listener -- but `document`
    // receives every product's variant:update event on the page, not just
    // this PDP's. Horizon's own ProductFormComponent#onVariantUpdate guards
    // against exactly this by comparing event.detail.data.productId (or
    // .newProduct.id for a combined-listing swap) to its own
    // this.dataset.productId before touching anything; apply the identical
    // guard here, or an unrelated product's variant change (e.g. a
    // related-product upsell block) could silently overwrite the tracked
    // availability for the product actually being purchased.
    const eventProductId = event.detail?.data?.newProduct?.id ?? event.detail?.data?.productId;
    if (!this.productForm || eventProductId !== this.productForm.dataset.productId) return;

    // Horizon's own #onVariantUpdate has already disabled/enabled the
    // button for stock by the time this document-level listener runs
    // (bubble phase, registered after Horizon's closer-ancestor listener --
    // see the class doc comment). Track that availability directly from the
    // event payload rather than re-reading the DOM, since this gate's own
    // render() below is about to write to that same disabled attribute.
    this.variantAvailable = event.detail?.resource != null && event.detail.resource.available !== false;
    this.render();
  }

  // Acknowledging an unverified/OE-only purchase is scoped to one vehicle
  // claim; a vehicle change must never let a stale acknowledgement silently
  // authorize a purchase evaluated against a different vehicle.
  handleVehicleChange = () => {
    this.buyingByOem = false;
    this.acknowledged = false;
    this.refs.acknowledgement.checked = false;
    this.render();
  };

  handleStorage = (event) => {
    if (event.key === GARAGE_STORAGE_KEY) this.handleVehicleChange();
  };

  chooseOemPath() {
    this.buyingByOem = true;
    this.render();
  }

  acknowledge(event) {
    this.acknowledged = event.target.checked === true;
    this.render();
  }

  decide() {
    const applications = parseApplications(this.refs.applicationsData);
    const fitmentState = evaluateFitment(loadGarage(), applications);
    return purchaseGateDecision({
      fitmentState,
      riskLevel: this.dataset.riskLevel,
      buyingByOem: this.buyingByOem,
      acknowledged: this.acknowledged
    });
  }

  render() {
    const decision = this.decide();
    this.refs.acknowledgementContainer.hidden = !decision.requiresAcknowledgement;
    this.refs.liveRegion.textContent = LIVE_REGION_MESSAGES[decision.reason] || '';
    this.applyToProductForm(decision);
  }

  applyToProductForm(decision) {
    // Fail closed: if the Product Form cannot be found, there is nothing to
    // safely re-enable, so the button state Horizon rendered stays untouched
    // rather than assuming a purchase path exists.
    if (!this.form) return;

    // This gate only ever adds restriction on top of Horizon's own stock
    // state -- a canPurchase:true Fitment decision must never be the reason
    // a sold-out/unavailable variant's Add to Cart becomes enabled.
    const disabled = !decision.canPurchase || !this.variantAvailable;

    for (const addToCart of this.form.querySelectorAll('add-to-cart-component')) {
      const button = addToCart.refs?.addToCartButton ?? addToCart.querySelector('[ref="addToCartButton"]');
      if (!button) continue;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }

    for (const paymentElement of this.form.querySelectorAll('.shopify-payment-button, .accelerated-checkout-block')) {
      paymentElement.hidden = disabled;
    }
  }

  guardSubmit(event) {
    if (!this.decide().canPurchase || !this.variantAvailable) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}

if (!customElements.get('motivelle-purchase-gate')) {
  customElements.define('motivelle-purchase-gate', MotivellePurchaseGate);
}
